import React, { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from "chart.js";
import "./ViewResult.css";

ChartJS.register(ArcElement, Tooltip, Legend);

const API_BASE = "https://rsa.feetracer.in/app";
const GET_EXPLANATION_ENDPOINT = `${API_BASE}/get_explanation.php`;

function optionIndexToLetter(i) {
  return String.fromCharCode(65 + i); // 0 → A
}

const ViewResult = ({ examStartId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    if (!examStartId) {
      setError("Missing exam ID");
      setLoading(false);
      return;
    }

    fetch(`${GET_EXPLANATION_ENDPOINT}?id=${encodeURIComponent(examStartId)}`)
      .then((res) => res.text())
      .then((txt) => {
        const trimmed = txt.trim();
        if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
          throw new Error("Server returned non-JSON response");
        }
        return JSON.parse(trimmed);
      })
      .then((data) => {
        if (!data.success) throw new Error(data.message);
        setPayload(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [examStartId]);

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!payload) return <div className="error">No data found</div>;

  const { exam_meta, counts, questions } = payload;

  const examType = exam_meta.exam_type; // "M" / "C"
  const isChapterExam = examType === "C";

  const subject = exam_meta.subject_name || "";
  const chapter = exam_meta.chapter_name || "";

  const examTitle = examType === "C"
    ? `Chapter Exam – ${subject} # ${chapter}`
    : `Master Exam – ${subject}`;

  // =======================
  // CHART CONFIG
  // MASTER EXAM → Remove Skipped slice only
  // =======================

  const chartLabels = isChapterExam
    ? ["Correct", "Wrong", "Skipped", "Not Attempted"]
    : ["Correct", "Wrong", "Not Attempted"];

  const chartValues = isChapterExam
    ? [counts.correct, counts.wrong, counts.skipped, counts.notAttempted]
    : [counts.correct, counts.wrong, counts.notAttempted];

  const chartColors = isChapterExam
    ? ["#29b36b", "#e74c3c", "#f59e0b", "#6b7280"]
    : ["#29b36b", "#e74c3c", "#6b7280"];

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        data: chartValues,
        backgroundColor: chartColors,
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    plugins: { legend: { display: false } },
    maintainAspectRatio: false
  };

  return (
    <section>
      <div className="view-result-modal">
        <div className="view-result-wrapper">

          {/* HEADER */}
          <div className="vr-header">
            <div className="vr-left">
              <h4 className="vr-title">{examTitle}</h4>

              <div className="vr-meta">
                <span>Start: {exam_meta.start_time}</span>
                <span>End: {exam_meta.end_time}</span>
                <span>Duration: {exam_meta.duration}</span>

              </div>

              <div className="vr-counts">
                <span className="c-correct">Correct: {counts.correct}</span>
                <span className="c-wrong">Wrong: {counts.wrong}</span>

                {isChapterExam && (
                  <span className="c-skip">Skipped: {counts.skipped}</span>
                )}

                <span className="c-na">Not Attempted: {counts.notAttempted}</span>

                {isChapterExam && (
                  <span className="c-bookmark">Bookmarks: {counts.bookmark}</span>
                )}
              </div>
            </div>

            {/* RIGHT SIDE → GRAPH + FULL LEGEND */}
            <div className="vr-right">
              <div className="vr-chart-legend">
                <div className="vr-chart">
                  <div className="vr-chart-wrapper">
                    <Doughnut data={chartData} options={chartOptions} />
                  </div>
                </div>

                {/* LEGEND ALWAYS VISIBLE (skip removed only for master) */}
                <div className="vr-legend">
                  <div className="legend-item">
                    <span className="legend-swatch sw-correct"></span>
                    <span>Correct</span>
                    <span className="legend-count">{counts.correct}</span>
                  </div>

                  <div className="legend-item">
                    <span className="legend-swatch sw-wrong"></span>
                    <span>Wrong</span>
                    <span className="legend-count">{counts.wrong}</span>
                  </div>

                  {isChapterExam && (
                    <div className="legend-item">
                      <span className="legend-swatch sw-skipped"></span>
                      <span>Skipped</span>
                      <span className="legend-count">{counts.skipped}</span>
                    </div>
                  )}

                  <div className="legend-item">
                    <span className="legend-swatch sw-na"></span>
                    <span>Not Attempted</span>
                    <span className="legend-count">{counts.notAttempted}</span>
                  </div>

                  {isChapterExam && (
                    <div className="legend-item">
                      <span className="legend-swatch" style={{ background: "#2563eb" }}></span>
                      <span>Bookmarks</span>
                      <span className="legend-count">{counts.bookmark}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* =======================
            QUESTIONS
        ======================= */}
          <div className="view-result-body">
            {questions.map((q, idx) => {
              const correctKey = q.correct_answer;
              const correctIndex = q.options.findIndex(o => o.key === correctKey);
              const selectedIndex = q.options.findIndex(o => o.key === q.selected_option);

              return (
                <div className="explanation-card" key={q.exam_answer_id || idx}>
                  <div className="question-header">
                    <h3 className="question-title">
                      {idx + 1}. {q.question}
                    </h3>

                    <div className="quiz-id-text">
                      Quiz ID: {q.quiz_id || "—"}
                    </div>
                  </div>

                  <ul className="choices-list">
                    {q.options.map((opt, i) => {
                      let cls = "choice-item";

                      if (i === correctIndex) cls += " correct";         // green
                      if (i === selectedIndex && i !== correctIndex) cls += " wrong"; // red
                      if (i === selectedIndex && i === correctIndex) cls += " correct"; // green

                      return (
                        <li className={cls} key={opt.key}>
                          <div className="option-label">
                            {optionIndexToLetter(i)}.
                          </div>

                          <div className="choice-text">
                            {opt.label}
                          </div>

                          <div className="choice-badge">
                            {i === selectedIndex && i !== correctIndex && "Your Answer"}
                            {i === correctIndex && i !== selectedIndex && "Correct"}
                            {i === selectedIndex && i === correctIndex && "Correct Answer"}
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  {q.explanation && (
                    <div className="explanation-text">
                      <strong>Explanation:</strong> {q.explanation}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ViewResult;
